import { PhoneNumberFormat, PhoneNumberUtil } from "google-libphonenumber";

const phoneUtil = PhoneNumberUtil.getInstance();
const { ValidationResult } = PhoneNumberUtil;

/**
 * Returns a user-facing validation message if the phone number is invalid,
 * or null if it is valid. Accepts local NZ numbers and E.164 format.
 */
export function getPhoneValidationMessage(phone: string): string | null {
	let parsed;
	try {
		parsed = phoneUtil.parse(phone, "NZ");
	} catch (e: unknown) {
		if (e instanceof Error) {
			if (e.message.includes("TOO_SHORT")) {
				return "Phone number is too short";
			}
			if (e.message.includes("TOO_LONG")) {
				return "Phone number is too long";
			}
			if (e.message.includes("INVALID_COUNTRY_CODE")) {
				return "Invalid country code";
			}
		}
		return "Enter a valid phone number";
	}

	const result = phoneUtil.isPossibleNumberWithReason(parsed);

	switch (result) {
		case ValidationResult.TOO_SHORT:
			return "Phone number is too short";
		case ValidationResult.TOO_LONG:
			return "Phone number is too long";
		case ValidationResult.INVALID_COUNTRY_CODE:
			return "Invalid country code";
		case ValidationResult.INVALID_LENGTH:
			return "Phone number length is invalid";
		case ValidationResult.IS_POSSIBLE:
		case ValidationResult.IS_POSSIBLE_LOCAL_ONLY:
			return phoneUtil.isValidNumber(parsed) ? null : "Enter a valid phone number";
		default:
			return "Enter a valid phone number";
	}
}

/**
 * Normalises a local NZ or E.164 phone number to E.164 format.
 * Returns the input unchanged if parsing fails.
 */
export function toE164Phone(phone: string): string {
	try {
		const parsed = phoneUtil.parse(phone, "NZ");
		return phoneUtil.format(parsed, PhoneNumberFormat.E164);
	} catch {
		return phone;
	}
}

/**
 * Normalises a local phone number using an explicit E.164 prefix (e.g. "+64").
 * Returns the national significant digits only (e164 minus the prefix).
 * Returns localNumber unchanged if the prefix is absent or parsing fails.
 */
export function normalizePhoneWithPrefix(prefix: string, localNumber: string): string {
	if (!prefix.trim() || !localNumber.trim()) {
		return localNumber;
	}
	try {
		const parsed = phoneUtil.parse(`${prefix}${localNumber}`);
		const e164 = phoneUtil.format(parsed, PhoneNumberFormat.E164);
		return e164.slice(prefix.length);
	} catch {
		return localNumber;
	}
}
